// @flow
import R from 'ramda';
import { find, uniqWith, sortBy } from 'lodash';
import _debug from 'debug';
import queryString from 'query-string';
import { reduce, dateFromPair, milesToMeters } from '../helpers/utils';
import typeof Client from '../api/Api';
import { isReservable, isJp } from '../components/Search/helpers';

const debug = _debug('jp:reducers:search');

type LatLng = {
  lat: number,
  lng: number
};

type ParkingType = 'onstreet' | 'offstreet';

export type Result = {
  coordinates: LatLng,
  id: number,
  photos: Array<string>,
  price: {
    amount: number,
    currency: string,
    formatted: string
  },
  reviews: {
    rating: number,
    count: number
  },
  title: string,
  type: ParkingType,
  slug?: string,
  cluster?: boolean,
  availability: ?number,
  distance: number
};

type SearchFormData = {
  q: string,
  coords: LatLng,
  placeId: string,
  dates: Array<{
    arriving: Date,
    leaving: Date
  }>
};

type Filter =
  | {|
      type: 'select',
      value: 'all' | 'driveways' | 'car_parks'
    |}
  | {|
      type: 'with',
      value:
        | 'airport_transfer'
        | 'onsite_staff'
        | 'disabled_access'
        | 'cctv'
        | 'secure_access'
        | 'multiple_entry'
    |}
  | {|
      type: 'sort',
      value: 'best_match' | 'distance'
    |};

export const filters = {
  select: {
    all: { type: 'select', value: 'all' },
    driveways: { type: 'select', value: 'driveways' },
    car_parks: { type: 'select', value: 'car_parks' }
  },
  with: {
    airport_transfer: { type: 'with', value: 'airport_transfer' },
    onsite_staff: { type: 'with', value: 'onsite_staff' },
    disabled_access: { type: 'with', value: 'disabled_access' },
    cctv: { type: 'with', value: 'cctv' },
    secure_access: { type: 'with', value: 'secure_access' },
    multiple_entry: { type: 'with', value: 'multiple_entry' }
  },
  sort: {
    best_match: { type: 'sort', value: 'best_match' },
    distance: { type: 'sort', value: 'distance' }
  }
};

type SelectType = 'all' | 'driveways' | 'car_parks';

/* could do something sexier by making values match the values used in
  the data but that is not as clear as a declarative definition of what
  each filter does */
const getSelectFunc = ({ value }: { value: SelectType }) => {
  switch (value) {
    case 'all': {
      return R.identity;
    }
    case 'driveways': {
      return R.filter((v: { type: ParkingType }) => v.type === 'onstreet');
    }
    case 'car_parks': {
      return R.filter((v: { type: ParkingType }) => v.type === 'offstreet');
    }
    default:
      throw new Error('Unknown select filter type');
  }
};

const getWithFunc = () => R.identity;

const distance = (
  { lat: lat1, lng: lng1 }: LatLng,
  { coordinates: { lat: lat2, lng: lng2 } }: { coordinates: LatLng }
) => {
  const deglen = 110.25;
  const x = lat1 - lat2;
  const y = (lng1 - lng2) * Math.cos(lat2);
  return deglen * Math.sqrt(x * x + y * y);
};

const getSortFunc = (center, { value }) => {
  if (value === 'best_match') {
    return R.identity;
  }
  return R.sortBy(v => distance(center, v));
};

const getFilterFunc = (center, filter) => {
  switch (filter.type) {
    case 'select': {
      return getSelectFunc(filter);
    }
    case 'with': {
      return getWithFunc();
    }
    case 'sort': {
      return getSortFunc(center, filter);
    }
    default: {
      throw new Error('Unknown filter type');
    }
  }
};

const makeFilters: any = (center, newFilters) =>
  R.reduce(
    (f, fi) =>
      R.compose(
        f,
        getFilterFunc(center, fi)
      ),
    R.identity,
    newFilters
  );

export type State = {|
  selectedId: string,
  datePickerVisible: boolean,
  showLoginWall: boolean,
  showNonReservableNote: boolean,
  showLocationDetails: boolean,
  showReviews: boolean,
  selectedFirstPin: boolean,
  selectedPinDirty: boolean,
  selectedLocationDetail: boolean,
  modalAccordion: boolean,
  showHowItWorks: boolean,
  searchDirty: boolean,
  loadingPins: boolean,
  details: { [number]: any },
  pinResults: Array<Result>,
  dismissedTooltip: boolean,
  savedSearch: ?SearchFormData,
  hasSearched: SearchFormData,
  clientSideSearch: boolean,
  wantScroll: boolean,
  showEditSearch: boolean,
  clusters: Array<number>,
  lowestPrice: number,
  filters: Array<Filter>,
  filterResults: Array<Result>,
  zoom: number,
  metadata: {
    title: string,
    description: string,
    canonical_url: string
  },
  currentDistanceFromCenter: number,
  currentPanPosition: { lat: number, lng: number },
  dragging: boolean,
  searchTimestamp: number,
  firstLoadComplete: boolean,
  searchInitBy?: string,
  searchInitByValue?: string
|};

const defaultArriving: Date = new Date();
defaultArriving.setHours(8);
defaultArriving.setMinutes(0);

const defaultLeaving: Date = new Date();
defaultLeaving.setHours(18);
defaultLeaving.setMinutes(0);

export const initialState: () => State = () => ({
  selectedId: '_:-1',
  datePickerVisible: false,
  showLoginWall: false,
  showNonReservableNote: false,
  showLocationDetails: false,
  showReviews: false,
  selectedFirstPin: false,
  selectedPinDirty: false,
  selectedLocationDetail: false,
  dismissedTooltip: false,
  searchDirty: false,
  savedSearch: null,
  modalAccordion: false,
  showHowItWorks: false,
  loadingPins: true,
  details: {},
  pinResults: [],
  hasSearched: {
    q: '',
    placeId: '',
    dates: [
      {
        arriving: defaultArriving,
        leaving: defaultLeaving
      }
    ],
    coords: {
      lat: 51.48918,
      lng: -0.212642
    }
  },
  clientSideSearch: false,
  wantScroll: false,
  showEditSearch: false,
  clusters: [],
  filters: [],
  filterResults: [],
  lowestPrice: 100,
  zoom: 15,
  metadata: {
    title: '',
    description: '',
    canonical_url: ''
  },
  currentDistanceFromCenter: 0,
  currentPanPosition: {
    lat: 51.48918,
    lng: -0.212642
  },
  dragging: false,
  firstLoadComplete: false
});

const SELECT_ITEM = 'search/SELECT_ITEM';
const TOGGLE_DATEPICKER = 'search/TOGGLE_DATEPICKER';
const TOGGLE_MODAL_ACCORDION = 'search/TOGGLE_MODAL_ACCORDION';
const TOGGLE_HOW_IT_WORKS = 'search/TOGGLE_HOW_IT_WORKS';
const TOGGLE_LOCATION_DETAILS = 'search/TOGGLE_LOCATION_DETAILS';
const TOGGLE_LOGIN_WALL = 'search/TOGGLE_LOGIN_WALL';
const LOAD_PINS = 'search/LOAD_PINS';
const LOAD_PINS_SUCCESS = 'search/LOAD_PINS_SUCCESS';
const LOAD_PINS_FAIL = 'search/LOAD_PINS_FAIL';
export const SEARCH_AREA = 'search/SEARCH_AREA';
const SET_AREA = 'search/SET_AREA';
const MAP_SELECT_ITEM = 'search/MAP_SELECT_ITEM';
const TOGGLE_EDIT_SEARCH = 'search/TOGGLE_EDIT_SEARCH';
const LOAD_REVIEW = 'search/LOAD_REVIEW';
const LOAD_REVIEW_SUCCESS = 'search/LOAD_REVIEW_SUCCESS';
const LOAD_REVIEW_FAIL = 'search/LOAD_REVIEW_FAIL';
const CLOSE_REVIEW = 'search/CLOSE_REVIEW';
const UPDATE_CLUSTERS = 'search/UPDATE_CLUSTERS';
const LOAD_DETAILS_FAIL = 'search/LOAD_DETAILS_FAIL';
const LOAD_DETAILS_SUCCESS = 'search/LOAD_DETAILS_SUCCESS';
const CLOSE_DETAILS = 'search/CLOSE_DETAILS';
const TOGGLE_FILTER = 'search/TOGGLE_FILTER';
const SET_ZOOM = 'search/SET_ZOOM';
const DISMISSED_TOOLTIP = 'search/DISMISSED_TOOLTIP';
const CLOSE_ALL_PANELS = 'search/CLOSE_ALL_PANELS';
const PANNED = 'search/PANNED';
const REDO_SEARCH = 'search/REDO_SEARCH';
const SET_CENTER = 'search/SET_CENTER';
const START_DRAG = 'search/START_DRAG';
const SET_DATE = 'search/SET_DATE';
const SET_DIRTY = 'search/SET_DIRTY';
const TOGGLE_NONRESERVABLE_NOTE = 'search/TOGGLE_NONRESERVABLE_NOTE';
const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const SET_SEARCH_INITIALISER = 'search/SET_SEARCH_INITIALISER';

export type Action =
  | {| type: typeof SELECT_ITEM, id: number |}
  | {| type: typeof TOGGLE_DATEPICKER |}
  | {| type: typeof TOGGLE_MODAL_ACCORDION |}
  | {| type: typeof TOGGLE_HOW_IT_WORKS |}
  | {| type: typeof TOGGLE_LOCATION_DETAILS |}
  | {| type: typeof TOGGLE_LOGIN_WALL |}
  | {| type: typeof LOAD_PINS |}
  | {| type: typeof SET_AREA, area: Object |}
  | {| type: typeof LOAD_PINS_SUCCESS, payload: Object |}
  | {| type: typeof LOAD_PINS_FAIL |}
  | {| type: typeof SEARCH_AREA, area: SearchFormData |}
  | {| type: typeof MAP_SELECT_ITEM, id: number |}
  | {| type: typeof TOGGLE_EDIT_SEARCH |}
  | {| type: typeof LOAD_REVIEW |}
  | {| type: typeof LOAD_REVIEW_SUCCESS |}
  | {| type: typeof LOAD_REVIEW_FAIL |}
  | {| type: typeof CLOSE_REVIEW |}
  | {| type: typeof UPDATE_CLUSTERS, ids: Array<number>, selected: string |}
  | {| type: typeof LOAD_DETAILS_FAIL |}
  | {| type: typeof LOAD_DETAILS_SUCCESS |}
  | {| type: typeof CLOSE_DETAILS |}
  | {| type: typeof TOGGLE_FILTER, filter: Filter |}
  | {| type: typeof SET_ZOOM, zoom: number |}
  | {| type: typeof DISMISSED_TOOLTIP |}
  | {| type: typeof PANNED, payload: { lat: number, lng: number } |}
  | {| type: typeof REDO_SEARCH |}
  | {| type: typeof CLOSE_ALL_PANELS |}
  | {| type: typeof START_DRAG |}
  | {| type: typeof SET_DIRTY |}
  | {| type: typeof TOGGLE_NONRESERVABLE_NOTE |}
  | {| type: typeof SET_DATE, payload: { arriving: Date, leaving: Date } |}
  | {| type: typeof SET_CENTER, payload: { lat: number, lng: number } |}
  | {|
      type: typeof SET_SEARCH_INITIALISER,
      payload: { name: string, value: string }
    |}
  | {|
      type: typeof LOCATION_CHANGE,
      payload: { pathname: string, search: string, hash: string, key: string }
    |};

export const parseArea = (
  state: Object,
  action: { type: typeof SEARCH_AREA, area: Object }
) => {
  const area = JSON.parse(JSON.stringify(action.area));

  if (!area.coords) {
    return state;
  }

  const [lat, lng] = area.coords.split(',').map(v => parseFloat(v));

  let arriving;
  let leaving;

  if (
    !area.arriving &&
    area.start_date &&
    area.start_time &&
    area.end_date &&
    area.end_time
  ) {
    arriving = dateFromPair(area.start_date, area.start_time);
    leaving = dateFromPair(area.end_date, area.end_time);
  } else {
    arriving = area.arriving ? new Date(area.arriving) : defaultArriving;
    leaving = area.leaving ? new Date(area.leaving) : defaultLeaving;
  }

  return {
    ...state,
    hasSearched: {
      q: area.q,
      placeId: area.place_id,
      coords: {
        lat,
        lng
      },
      dates: [
        {
          leaving,
          arriving
        }
      ]
    }
  };
};

export default reduce(initialState(), {
  [START_DRAG](state, action: Action) {
    debug(START_DRAG, action);
    return {
      ...state,
      dragging: true
    };
  },
  [SET_CENTER](state, action) {
    const { lat, lng } = action.payload;
    return {
      ...state,
      currentDistanceFromCenter: 0,
      currentPanPosition: {
        lat,
        lng
      },
      hasSearched: {
        ...state.hasSearched,
        coords: { lat, lng }
      }
    };
  },
  [PANNED](state, action) {
    debug(PANNED, action);
    const { lat, lng } = state.hasSearched.coords;
    const { payload } = action;
    return {
      ...state,
      dragging: false,
      currentPanPosition: {
        lat: payload.lat,
        lng: payload.lng
      },
      currentDistanceFromCenter: distance(
        { lat, lng },
        {
          coordinates: {
            lat: payload.lat,
            lng: payload.lng
          }
        }
      )
    };
  },
  [SELECT_ITEM](state, action) {
    debug(SELECT_ITEM);
    const { id } = action;
    return {
      ...state,
      selectedId: id,
      wantScroll: false,
      showLocationDetails: true,
      selectedLocationDetail: true,
      showReviews: id === state.selectedId ? state.showReviews : false
    };
  },
  [MAP_SELECT_ITEM](state, action) {
    debug(MAP_SELECT_ITEM);
    const { id, mobile } = action;
    return {
      ...state,
      selectedId: id,
      wantScroll: true,
      selectedFirstPin: !state.selectedPinDirty && !state.selectedFirstPin,
      selectedPinDirty: true,
      showLocationDetails: !mobile,
      showReviews: id === state.selectedId ? state.showReviews : false
    };
  },
  [TOGGLE_DATEPICKER](state) {
    debug(TOGGLE_DATEPICKER);
    const { datePickerVisible } = state;
    return {
      ...state,
      datePickerVisible: !datePickerVisible
    };
  },
  [TOGGLE_MODAL_ACCORDION](state) {
    debug(TOGGLE_MODAL_ACCORDION);
    const { modalAccordion } = state;
    return {
      ...state,
      modalAccordion: !modalAccordion
    };
  },
  [TOGGLE_HOW_IT_WORKS](state) {
    debug(TOGGLE_HOW_IT_WORKS);
    const { showHowItWorks } = state;
    return {
      ...state,
      showHowItWorks: !showHowItWorks
    };
  },
  [TOGGLE_LOCATION_DETAILS](state) {
    debug(TOGGLE_LOCATION_DETAILS);
    const { showLocationDetails } = state;
    return {
      ...state,
      showLocationDetails: !showLocationDetails
    };
  },
  [TOGGLE_LOGIN_WALL](state) {
    debug(TOGGLE_LOGIN_WALL);
    const { showLoginWall } = state;
    return {
      ...state,
      showLoginWall: !showLoginWall
    };
  },
  [CLOSE_REVIEW](state) {
    debug(CLOSE_REVIEW);
    return {
      ...state,
      showReviews: false
    };
  },
  [CLOSE_DETAILS](state) {
    debug(CLOSE_DETAILS);
    return {
      ...state,
      showLocationDetails: false
    };
  },
  [LOAD_REVIEW](state, action) {
    debug(LOAD_REVIEW);
    return {
      ...state,
      showReviews: action.show === undefined ? true : action.show
    };
  },
  [LOAD_REVIEW_SUCCESS](state, action) {
    debug(LOAD_REVIEW_SUCCESS);
    const {
      id,
      payload: { data }
    } = action;
    const results = state.pinResults.map(result => {
      if (result.id === id) {
        return {
          ...result,
          reviews: {
            ...result.reviews,
            data
          }
        };
      }
      return result;
    });
    const filterFunc = makeFilters(state.hasSearched.coords, state.filters);
    return {
      ...state,
      pinResults: results,
      details: {
        ...state.details,
        [id]: {
          ...state.details[id],
          reviews: data
        }
      },
      filterResults: filterFunc(results)
    };
  },
  [LOAD_PINS](state) {
    debug(LOAD_PINS);
    return {
      ...state,
      loadingPins: true,
      showReviews: false,
      showLocationDetails: false
    };
  },
  [SET_DIRTY](state) {
    return {
      ...state,
      searchDirty: true
    };
  },
  [LOAD_PINS_SUCCESS](state, action) {
    debug(LOAD_PINS_SUCCESS);
    const {
      payload: { data, meta }
    } = action;
    const upins = uniqWith(
      data,
      (arrVal, other) =>
        arrVal.coordinates.lat === other.coordinates.lat &&
        arrVal.coordinates.lng === other.coordinates.lng
    );
    const pinsSortedByDistance = sortBy(upins, ['distance']);
    const closestJustParkPin = find(pinsSortedByDistance, pin =>
      isReservable(pin)
    );
    const closestJustParkPinDistance = closestJustParkPin
      ? milesToMeters(closestJustParkPin.distance)
      : 0;

    let dataPins = 0;
    const fpins = upins.filter(pin => {
      // Hide any data pins when the first JustPark pin is within 500 meters
      if (
        closestJustParkPin &&
        closestJustParkPinDistance <= 500 &&
        !isJp(pin)
      ) {
        return false;
      }

      // Show a maximum of 4 data pins
      if (!isJp(pin)) {
        // We don't show data pins that are further away than the closest JustPark pin
        if (
          closestJustParkPin &&
          (dataPins > 3 || pin.distance > closestJustParkPin.distance)
        ) {
          return false;
        }
        dataPins += 1;
      }

      return true;
    });

    const pinResults = fpins || state.pinResults;
    const res = fpins || state.filterResults;
    const filterResults = res.map(result => ({
      ...result,
      distanceInMeters: milesToMeters(result.distance)
    }));

    return {
      ...state,
      loadingPins: false,
      metadata: meta,
      clientSideSearch: true,
      selectedId: '_:-1',
      pinResults,
      lowestPrice: pinResults.reduce(
        ([low, str], val) =>
          low > val.price.amount
            ? [val.price.amount, val.price.formatted]
            : [low, str],
        [Infinity, '']
      ),
      filterResults,
      firstLoadComplete: true
    };
  },
  [SET_AREA](state, action) {
    debug(SET_AREA, action);
    const parsed = parseArea(state, action);
    return {
      ...parsed,
      pinResults: [],
      currentPanPosition: {
        lat: parsed.hasSearched.coords.lat,
        lng: parsed.hasSearched.coords.lng
      }
    };
  },
  [SET_DATE](state, action) {
    debug(SET_DATE, action);
    return {
      ...state,
      searchDirty: true,
      savedSearch: state.savedSearch || state.hasSearched,
      hasSearched: {
        ...state.hasSearched,
        dates: action.payload
      }
    };
  },
  [SEARCH_AREA](state, action) {
    debug(SEARCH_AREA);
    return {
      ...parseArea(state, action),
      searchDirty: false,
      savedSearch: null,
      clientSideSearch: true,
      showEditSearch: false,
      searchTimestamp: +new Date()
    };
  },
  [TOGGLE_EDIT_SEARCH](state) {
    debug(TOGGLE_EDIT_SEARCH);
    return {
      ...state,
      wantScroll: false,
      searchDirty: false,
      savedSearch: null,
      hasSearched: state.savedSearched || state.hasSearched,
      showEditSearch: !state.showEditSearch,
      datePickerVisible: false
    };
  },
  [UPDATE_CLUSTERS](state, action) {
    debug(UPDATE_CLUSTERS);
    const { ids, selected } = action;
    if (ids.length === state.clusters.length && selected === state.selectedId) {
      return state;
    }
    return {
      ...state,
      clusters: ids
    };
  },
  [LOAD_DETAILS_SUCCESS](state, action) {
    debug(LOAD_DETAILS_SUCCESS);
    const {
      id,
      payload: { data }
    } = action;

    const result = find(state.pinResults, r => r.id === id);

    return {
      ...state,
      details: {
        ...state.details,
        [id]: {
          ...data,
          id,
          type:
            result.type === 'enquiry' ||
            result.type === 'driveup' ||
            result.type === 'driveup-onstreet'
              ? result.type
              : data.type,
          availability: result.availability,
          price: result.price
        },
        searchTimestamp: state.searchTimestamp
      }
    };
  },
  [TOGGLE_NONRESERVABLE_NOTE](state) {
    return {
      ...state,
      showNonReservableNote: !state.showNonReservableNote
    };
  },
  [TOGGLE_FILTER](state, action) {
    debug(TOGGLE_FILTER);
    const { filter } = action;
    if (
      state.filters.indexOf(filter) > -1 &&
      (filter.type !== 'select' && filter.type !== 'sort')
    ) {
      const newFilters = state.filters.filter(f => f !== filter);
      const filterFunc = makeFilters(state.hasSearched.coords, newFilters);
      return {
        ...state,
        showReviews: false,
        showLocationDetails: false,
        filters: newFilters,
        filterResults: filterFunc(state.pinResults)
      };
    }
    let newFilters = state.filters.slice();
    newFilters.unshift(filter);
    newFilters = R.uniqBy(
      f => (f.type === 'with' ? f.value : f.type),
      newFilters
    );
    const filterFunc = makeFilters(state.hasSearched.coords, newFilters);
    return {
      ...state,
      showReviews: false,
      showLocationDetails: false,
      filters: newFilters,
      filterResults: filterFunc(state.pinResults)
    };
  },
  [SET_ZOOM](state, action) {
    debug(SET_ZOOM);
    const { zoom } = action;
    return {
      ...state,
      zoom
    };
  },
  [CLOSE_ALL_PANELS](state) {
    debug(CLOSE_ALL_PANELS);
    return {
      ...state,
      showLocationDetails: false,
      showReviews: false
    };
  },
  [DISMISSED_TOOLTIP](state) {
    debug(DISMISSED_TOOLTIP);
    return {
      ...state,
      dismissedTooltip: true
    };
  },
  [LOCATION_CHANGE](state, action) {
    debug(LOCATION_CHANGE, action);
    const {
      payload: { search }
    } = action;

    const queryParams = { ...queryString.parse(search) };

    if (state.showReviews && !queryParams.reviews) {
      return {
        ...state,
        showReviews: false
      };
    }

    if (state.selectedId && state.showLocationDetails) {
      if (!queryParams.listingId && !queryParams.listingType) {
        return {
          ...state,
          selectedId: null,
          showLocationDetails: false,
          showReviews: false
        };
      }
    }

    return {
      ...state
    };
  },
  [SET_SEARCH_INITIALISER](state, action) {
    debug(SET_SEARCH_INITIALISER, action);
    return {
      ...state,
      searchInitBy: action.payload.name,
      searchInitByValue: action.payload.value
    };
  }
});

export const setSearchInitialiser = (name: string, value: string): Action => ({
  type: SET_SEARCH_INITIALISER,
  payload: {
    name,
    value
  }
});

export const toggleDatepicker = (): Action => ({ type: TOGGLE_DATEPICKER });

export const toggleModalAccordion = (): Action => ({
  type: TOGGLE_MODAL_ACCORDION
});

export const toggleNonreservableNote = (): Action => ({
  type: TOGGLE_NONRESERVABLE_NOTE
});

export const toggleHowItWorks = (): Action => ({ type: TOGGLE_HOW_IT_WORKS });

export const toggleLocationDetails = (): Action => ({
  type: TOGGLE_LOCATION_DETAILS
});

export const toggleLoginWall = (): Action => ({ type: TOGGLE_LOGIN_WALL });

export const searchArea = (area: SearchFormData): Action => ({
  type: SEARCH_AREA,
  area
});

export function setArea(area: Object) {
  return (dispatch: (*) => *) => {
    dispatch({ type: SET_AREA, area });
    return new Promise(resolve => setTimeout(resolve, 0));
  };
}

export const toggleEditSearch = (): Action => ({ type: TOGGLE_EDIT_SEARCH });

export const showReview = (): Action => ({ type: LOAD_REVIEW });

export const closeReview = (): Action => ({ type: CLOSE_REVIEW });

export const updateClusters = (
  ids: Array<number>,
  selected: string
): Action => ({ type: UPDATE_CLUSTERS, ids, selected });

export const closeDetails = (): Action => ({ type: CLOSE_DETAILS });

export const setDirty = (): Action => ({ type: SET_DIRTY });

export const toggleFilter = (filter: Filter): Action => ({
  type: TOGGLE_FILTER,
  filter
});

export const setPanned = (payload: { lat: number, lng: number }) => ({
  type: PANNED,
  payload
});

export const onStartDrag = () => ({ type: START_DRAG });

export function redoSearch() {
  return (dispatch: any, getState: any) => {
    dispatch({
      type: SET_CENTER,
      payload: getState().search.currentPanPosition
    });
    dispatch({
      types: [LOAD_PINS, LOAD_PINS_SUCCESS, LOAD_PINS_FAIL],
      promise: client => {
        const params = getState().search.hasSearched;
        return client.geo.search({
          lat: params.coords.lat,
          lng: params.coords.lng,
          start_date: params.dates[0].arriving.toISOString(),
          end_date: params.dates[0].leaving.toISOString(),
          google_place_id: params.placeId,
          user_query: params.q
        });
      }
    });
  };
}

export const toggleTooltip = (): Action => ({ type: DISMISSED_TOOLTIP });

export const setZoom = (zoom: number): Action => ({ type: SET_ZOOM, zoom });

export const setDate = (dates: Object): Action => ({
  type: SET_DATE,
  payload: dates
});

export const closeAllPanels = (): Action => ({ type: CLOSE_ALL_PANELS });

export function loadReviews(type: string, id: number, show?: boolean = true) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) => {
    const item = getState().search.pinResults.filter(r => r.id === id)[0];
    if (item.reviews.count > 0) {
      dispatch({
        id,
        show,
        types: [LOAD_REVIEW, LOAD_REVIEW_SUCCESS, LOAD_REVIEW_FAIL],
        promise: (client: Client) => client.locationDetails.reviews(item.id)
      });
    }
  };
}

export function selectItem(type: string, id: number) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) => {
    const state = getState();
    const item = state.search.details[id];

    if (!item || item.searchTimestamp !== state.search.searchTimestamp) {
      dispatch({
        id,
        types: [SELECT_ITEM, LOAD_DETAILS_SUCCESS, LOAD_DETAILS_FAIL],
        promise: (client: Client) =>
          client.locationDetails.info(type, id).then(details => {
            if (
              details.data.feedback &&
              details.data.feedback.count > 0 &&
              details.data.feedback.latestReview &&
              parseFloat(details.data.feedback.latestReview.rating) < 4
            ) {
              dispatch(loadReviews(details.data.type, details.data.id, false));
            }
            return details;
          })
      });
    } else {
      dispatch({ type: SELECT_ITEM, id });
    }
  };
}

export function loadPins(
  enquiries: number = 0,
  loadListing = false,
  listingType,
  listingId
) {
  return (dispatch: (*) => Promise<*>, getState: () => { search: State }) =>
    dispatch({
      types: [LOAD_PINS, LOAD_PINS_SUCCESS, LOAD_PINS_FAIL],
      promise: (client: Client) => {
        const params = (getState ? getState().search : initialState())
          .hasSearched;
        return client.geo
          .search({
            lat: params.coords.lat,
            lng: params.coords.lng,
            start_date: params.dates[0].arriving.toISOString(),
            end_date: params.dates[0].leaving.toISOString(),
            google_place_id: params.placeId,
            user_query: params.q,
            enquiries
          })
          .then(response => {
            // Handles the case where we have a listingId thats stored in a
            // queryParam to trigger the load of details for that result
            if (loadListing) {
              setImmediate(() => {
                dispatch(selectItem(listingType, listingId));
              });
            }
            return response;
          });
      }
    });
}

export function mapSelectItem(type: string, id: number) {
  return (
    dispatch: (*) => Promise<*>,
    getState: () => { search: State, browser: Object }
  ) => {
    const state = getState();
    const item = state.search.details[id];
    const mobile = state.browser.is.extraSmall;
    if (!item || item.searchTimestamp !== state.search.searchTimestamp) {
      dispatch({
        id,
        mobile,
        types: [MAP_SELECT_ITEM, LOAD_DETAILS_SUCCESS, LOAD_DETAILS_FAIL],
        promise: (client: Client) => client.locationDetails.info(type, id)
      });
    } else {
      dispatch({ type: MAP_SELECT_ITEM, id, mobile });
    }
  };
}

type Analytics = {|
  track: (string, ?{}, ?{}) => void,
  page: (string, ?{}, ?{}) => void
|};

export const analyticsEventSubscribers = {
  [TOGGLE_FILTER]: (action: Action, analytics: Analytics) => {
    if (action.type === TOGGLE_FILTER) {
      if (action.filter.type === 'sort') {
        analytics.track('action:search-results:sorting-changed', action.filter);
      }
      if (action.filter.type === 'select') {
        analytics.track('action:search-results:filter-set', action.filter);
      }
    }
  },
  [SELECT_ITEM]: (action: Action, analytics: Analytics) =>
    action.type === SELECT_ITEM &&
    analytics.page('listing-details', { listingId: action.id }),
  [MAP_SELECT_ITEM]: (action: Action, analytics: Analytics, getState: Func) => {
    if (action.type !== MAP_SELECT_ITEM) {
      return;
    }

    const { browser } = getState();

    if (browser.greaterThan.small) {
      analytics.page('listing-details', { listingId: action.id });
    }
  }
};
